import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

import { PlanHeader } from '../components/common/plan-header';
import { PlanActionsButton } from '../components/common/actions-button';
import { HandsontableCommon } from '../components/common/handsontable-common';
import { useRegisterPlan } from '../hooks/use-register-plan';
import { HandsontableThreeLine } from '../components/three-line/handsontable-three-line';
import {
  createSwitchToSingleLineHandler,
  createSwitchToThreeLineHandler,
} from '../components/common/handsontable-helpers';
import { usePlanRegisterUnsavedChanges } from '../hooks/use-plan-unsaved-changes';

import { usePageExitReset } from '@/features/companies/hooks/use-page-exit-reset';
import { getOptionKey } from '@/utils/helpers';

export const PlanRegisterPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Use custom hook for all form logic
  const {
    tableData,
    tableData3Line,
    selectedProperty,
    setSelectedProperty,
    plantOptions,
    witnessCompanyOptions,
    propertyOptions,
    optionsLoaded,
    isValid,
    rowsWithErrors,
    handleCancel,
    handleSubmit,
    handleAddRow,
    handleAddRowAbove,
    handleAddRowBelow,
    handleDeleteRows,
    registerGetTableData,
    handleTableDataChange,
    registerGetTableData3Line,
    handleTableDataChange3Line,
    setLayoutSwitching,
    getTableDataRef,
    setRowsWithErrors,
    resetAllForms,
  } = useRegisterPlan();
  const [isThreeLineLayout, setIsThreeLineLayout] = useState(false);
  const previousLayoutRef = useRef<boolean>(false);

  // Check for unsaved changes (including table data and selectedProperty)
  const hasUnsavedChanges = usePlanRegisterUnsavedChanges(
    tableData,
    plantOptions,
    witnessCompanyOptions,
    selectedProperty,
    optionsLoaded
  );

  // Reset all forms when leaving page or reloading (with warning if unsaved changes)
  usePageExitReset(resetAllForms, true, hasUnsavedChanges);

  // Create handler for switching to single line layout
  const handleSwitchToSingleLine = useMemo(
    () =>
      createSwitchToSingleLineHandler({
        isThreeLineLayout,
        getTableDataRef,
        tableData3Line,
        handleTableDataChange3Line,
        setIsThreeLineLayout,
      }),
    [
      isThreeLineLayout,
      getTableDataRef,
      tableData3Line,
      handleTableDataChange3Line,
      setIsThreeLineLayout,
    ]
  );

  // Create handler for switching to three line layout
  const handleSwitchToThreeLine = useMemo(
    () =>
      createSwitchToThreeLineHandler({
        isThreeLineLayout,
        getTableDataRef,
        handleTableDataChange,
        setIsThreeLineLayout,
      }),
    [isThreeLineLayout, getTableDataRef, handleTableDataChange, setIsThreeLineLayout]
  );

  // Handle layout switching - set flag to prevent unnecessary state updates
  useEffect(() => {
    // Skip on initial mount
    if (previousLayoutRef.current === isThreeLineLayout) {
      previousLayoutRef.current = isThreeLineLayout;
      return;
    }
    setLayoutSwitching(true);

    // Reset flag after a short delay to allow data sync
    const timer = setTimeout(() => {
      setLayoutSwitching(false);
      previousLayoutRef.current = isThreeLineLayout;
    }, 100);

    return () => clearTimeout(timer);
  }, [isThreeLineLayout, setLayoutSwitching]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', py: 2, px: 2 }}>
      {/* Header */}
      <Box data-plan-header>
        <PlanHeader title='予定登録' subtitle='予定情報を入力してください' />
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Main Content */}
        <Box sx={{ flex: 1 }}>
          {/* Select Property Section */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }} data-input-section>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 1 auto' }}>
                <Typography variant='body1' sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  現場名称
                </Typography>
                <Autocomplete
                  options={propertyOptions}
                  getOptionKey={getOptionKey}
                  value={selectedProperty}
                  onChange={(_, newValue) => setSelectedProperty(newValue)}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.key === value?.key}
                  loading={!optionsLoaded}
                  sx={{
                    maxWidth: '400px',
                    minWidth: '560px',
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='入力ください'
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {!optionsLoaded ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                  noOptionsText='データが見つかりません'
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                <Button
                  size='large'
                  onClick={handleSwitchToSingleLine}
                  startIcon={!isThreeLineLayout ? <CheckIcon /> : null}
                  sx={{
                    minWidth: 120,
                    borderRadius: '100px',
                    height: '56px',
                    fontSize: '16px',
                    backgroundColor: !isThreeLineLayout ? '#002A76' : '#DBE1FF',
                    color: !isThreeLineLayout ? '#FFF' : '#002A76',
                  }}
                >
                  1行表示
                </Button>
                <Button
                  size='large'
                  onClick={handleSwitchToThreeLine}
                  startIcon={isThreeLineLayout ? <CheckIcon /> : null}
                  sx={{
                    minWidth: 120,
                    borderRadius: '100px',
                    height: '56px',
                    fontSize: '16px',
                    backgroundColor: isThreeLineLayout ? '#002A76' : '#DBE1FF',
                    color: isThreeLineLayout ? '#FFF' : '#002A76',
                  }}
                >
                  3行表示
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Handsontable Section */}
          {optionsLoaded &&
            (isThreeLineLayout ? (
              <HandsontableThreeLine
                tableData={tableData3Line}
                plantOptions={plantOptions}
                witnessCompanyOptions={witnessCompanyOptions}
                onDataChange={handleTableDataChange3Line}
                onGetTableData={registerGetTableData3Line}
                onAddRowAbove={handleAddRowAbove}
                onAddRowBelow={handleAddRowBelow}
                onDeleteRows={handleDeleteRows}
                rowsWithErrors={rowsWithErrors}
                setRowsWithErrors={setRowsWithErrors}
              />
            ) : (
              <HandsontableCommon
                tableData={tableData}
                plantOptions={plantOptions}
                witnessCompanyOptions={witnessCompanyOptions}
                onDataChange={handleTableDataChange}
                onGetTableData={registerGetTableData}
                onAddRowAbove={handleAddRowAbove}
                onAddRowBelow={handleAddRowBelow}
                onDeleteRows={handleDeleteRows}
                rowsWithErrors={rowsWithErrors}
                setRowsWithErrors={setRowsWithErrors}
              />
            ))}
        </Box>
      </Box>

      {/* Fixed Footer Actions */}
      <Box data-footer-buttons>
        <PlanActionsButton
          isSubmitting={false}
          isSubmitDisabled={!isValid}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          onAddRow={handleAddRow}
          submitLabel='入力内容を決定'
          canUpdate={true}
        />
      </Box>
    </Box>
  );
};
